export default class Camera {
  private video: HTMLVideoElement
  private stream: MediaStream | null = null
  private mediaRecorder: MediaRecorder | null = null
  private recordedChunks: Blob[] = []
  private isRecording = false

  constructor(videoElement: HTMLVideoElement) {
    this.video = videoElement
  }

  async start(): Promise<void> {
    try {
      const defaultConstrains: MediaStreamConstraints = {
        video: true,
        audio: true,
      }

      this.stream = await navigator.mediaDevices.getUserMedia(defaultConstrains)

      this.video.srcObject = this.stream
      await this.video.play()
    } catch (error) {
      console.error('Error accessing camera:', error)
      throw error
    }
  }

  stop(): void {
    if (this.isRecording) {
      this.mediaRecorder?.stop()
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
      this.video.srcObject = null
    }
  }

  capturePhoto(
    type: string = 'image/jpeg',
    quality: number = 1
  ): Promise<Blob> {
    const canvas = document.createElement('canvas')
    canvas.width = this.video.videoWidth
    canvas.height = this.video.videoHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }

    ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height)

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create blob'))
          }
        },
        type,
        quality
      )
    })
  }

  startRecording(options?: MediaRecorderOptions): void {
    if (!this.stream) {
      throw new Error('Camera stream not available')
    }

    if (this.isRecording) {
      console.warn('Already recording')
      return
    }

    const defaultOptions: MediaRecorderOptions = {
      mimeType: 'video/mp4',
      videoBitsPerSecond: 2500000,
    }

    const finalOptions = options || defaultOptions
    if (!MediaRecorder.isTypeSupported(finalOptions.mimeType || '')) {
      delete finalOptions.mimeType
    }

    this.mediaRecorder = new MediaRecorder(this.stream, finalOptions)
    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data)
      }
    }

    this.mediaRecorder.onstart = () => {
      this.isRecording = true
    }

    this.mediaRecorder.onstop = () => {
      this.isRecording = false
    }

    this.mediaRecorder.start()
    console.log('Recording started')
  }

  stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || !this.isRecording) {
        reject(new Error('No active recording'))
        return
      }

      this.mediaRecorder.onstop = () => {
        this.isRecording = false
        const blob = new Blob(this.recordedChunks, {
          type: this.mediaRecorder?.mimeType || 'video/webm',
        })
        resolve(blob)
      }

      this.mediaRecorder.stop()
    })
  }
}
